import {useInfluencerQuery} from "@/api/influencerApi";
import CheckIcon from "@/assets/colorable/check.svg?react";
import CityIcon from "@/assets/colorable/city.svg?react";
import InviteUser from "@/assets/colorable/invite_user.svg?react";
import MergeArrowIcon from "@/assets/colorable/merge_arrow.svg?react";
import PenEdit from "@/assets/colorable/pen_edit.svg?react";
import RightArrowIcon from "@/assets/colorable/right_arrow.svg?react";
import CompanyLogoPlaceholder from "@/assets/light_gradient/company_logo_placeholder.svg";
import Button from "@/components/Common/Button";
import Loader from "@/components/Common/kpi/Loader";
import {useLogout} from "@/hooks/useLogout";
import {useHelpUrl} from "@/hooks/useNavigationHooks";
import {
  useIsAdmin,
  useIsCompany,
  useIsInfuencer,
  useIsSubbrand,
  useSelectedCompanyReactive,
  useUserPermission,
} from "@/hooks/useUserHooks";
import HelpProfileIcon from "@/images/help_profile_icon.svg?react";
import InvoiceIcon from "@/images/invoice_icon.svg?react";
import LogoutProfileIcon from "@/images/logout_profile_icon.svg?react";
import CompanyIcon from "@/images/navbar_company_icon.svg?react";
import UserAvatarPlaceholder from "@/images/navbar_logged_out_profile_icon.svg";
import UserProfileIcon from "@/images/user_profile_icon.svg?react";
import clsxm from "@/lib/clsxm";
import {Can} from "@/permissions";
import {AGENCY_ROUTES_WITHOUT_NAVIGATION} from "@/store/constants";
import {editCompanyMemberModalAtom, multiAccountInviteModalAtom, selectedCompanyIdAtom} from "@/store/jotaiAtoms";
import useRegistrationStore from "@/store/registration";
import useUserStore from "@/store/user";
import {useSetAtom} from "jotai";
import {Fragment, useCallback, useMemo, type FC} from "react";
import {useTranslation} from "react-i18next";
import {Link, useNavigate} from "react-router";
import {useMedia} from "react-use";

const NavbarCompanyProfile: FC = () => {
  const {t} = useTranslation(["navigation", "multi_account"]);
  const navigate = useNavigate();
  const helpUrl = useHelpUrl();
  const screenHeight = useMedia("(min-height: 868px)");
  const setInviteModal = useSetAtom(multiAccountInviteModalAtom);
  const setEditCompanyMemberModal = useSetAtom(editCompanyMemberModalAtom);
  const setSelectedCompanyId = useSetAtom(selectedCompanyIdAtom);
  const user = useUserStore(state => state.user);
  const userPermission = useUserPermission();
  const isAdmin = useIsAdmin();
  const isInfluencer = useIsInfuencer();
  const selectedCompany = useSelectedCompanyReactive();
  const logout = useLogout();
  const setRegistrationValue = useRegistrationStore(state => state.setValue);

  const {data, isLoading} = useInfluencerQuery({
    id: Number(user.influencer_uuid),
  });
  const isCompany_ = useIsCompany();
  const isSubbrand = useIsSubbrand();
  const isCompany = isCompany_ || isSubbrand;

  const profileImage = useMemo(
    () => (isCompany ? (selectedCompany?.picture320 ?? selectedCompany?.picture) : data?.picture320),
    [isCompany, selectedCompany?.picture, selectedCompany?.picture320, data?.picture320],
  );
  const displayName = useMemo(
    () => (isCompany ? selectedCompany?.company_name : user.username),
    [isCompany, selectedCompany?.company_name, user.username],
  );
  const openUserProfile = useCallback(() => {
    setRegistrationValue("isEmailPrefilled", true);
    setRegistrationValue("email", user.email ?? "");
    setRegistrationValue("firstName", user.first_name ?? "");
    setRegistrationValue("lastName", user.last_name ?? "");
    setEditCompanyMemberModal({
      isVisible: true,
      isRegistering: false,
    });
  }, [setRegistrationValue, setEditCompanyMemberModal]);

  const multiAccountAdminButtons = useMemo(
    () =>
      isAdmin && (
        <>
          <Button
            onClick={() => navigate("/admins/agency/create/")}
            buttonStyle="Blue"
            className="mx-3 mb-1.5 h-auto gap-2 px-4 text-sm">
            <CityIcon className="size-3.5" />
            {t("create_new_multi_account_user")}
          </Button>
          <Button
            onClick={() => navigate("/admins/accounts/combine/")}
            buttonStyle="Blue"
            className="mx-3 mb-3 h-auto gap-2 px-4 text-sm">
            <MergeArrowIcon className="size-3.5" />
            {t("combine_accounts")}
          </Button>
        </>
      ),
    [isAdmin, navigate, t],
  );
  const userPill = useMemo(
    () => (
      <div className="flex flex-col">
        <button
          onClick={openUserProfile}
          className="border-neutral-grey-light-dark mx-3 mb-3 flex items-center justify-between gap-3 rounded-full border p-1 pr-2">
          <div className="flex items-center gap-3">
            <div className="border-neutral-grey-light-dark size-6 shrink-0 rounded-full border">
              <img
                src={user.profile_picture ?? UserAvatarPlaceholder}
                className="h-full w-full rounded-full object-fill"
              />
            </div>
            <span className="bg-linear-(--primary-dark-gradient) bg-clip-text font-semibold text-transparent">
              {user.first_name}
            </span>
          </div>
          <span className="text-neutral-black text-xs text-nowrap">
            {t(
              `multi_account:${(userPermission === "REFLUENCED_COMPANY_ADMIN" ? "ADMIN" : userPermission).toLowerCase()}_permissions`,
            )}
          </span>
        </button>
        {multiAccountAdminButtons}
      </div>
    ),
    [multiAccountAdminButtons, openUserProfile, t, user, userPermission],
  );

  return (
    <div className="group relative flex h-14 w-14 items-center pr-1 pl-2">
      <Link to={isCompany ? `/company/${selectedCompany?.id}/` : `/influencer/${user.influencer_uuid}/`}>
        <div
          className={
            "border-neutral-grey-light flex h-10 w-10 shrink-0 items-center justify-center rounded-full border " +
            (profileImage || isCompany ? "" : "p-1")
          }>
          {!(isLoading && !!data) ? (
            <img
              className="size-full rounded-full object-cover"
              src={profileImage ?? (isCompany ? CompanyLogoPlaceholder : UserAvatarPlaceholder)}
              alt="Profile"
              style={{borderStyle: "solid"}}
            />
          ) : (
            <Loader />
          )}
        </div>
      </Link>
      <div
        className={clsxm(
          "border-neutral-white-dark absolute top-14 -right-7 origin-top-right scale-0 flex-col rounded-md border bg-white opacity-0 drop-shadow-lg transition-all delay-100 duration-200 group-hover:opacity-100 lg:group-hover:scale-100",
          user.sub_brands?.length! > 1 ? "min-w-[18rem]" : "min-w-[16rem]",
        )}>
        <div className="flex flex-col border-b">
          {user.sub_brands?.length! > 1 ? (
            user.sub_brands?.map((subBrand, idx) => {
              const brand = subBrand.company;

              return (
                <Fragment key={brand.id}>
                  {idx === 0 && (
                    <>
                      <div className="h-3" />
                      {userPill}
                    </>
                  )}
                  <div
                    key={brand.id}
                    className={clsxm(
                      "flex shrink flex-row justify-between gap-3 px-3 py-4",
                      selectedCompany?.id === brand.id && "bg-primary-powder",
                    )}>
                    <div className="flex flex-row items-center gap-3">
                      <img
                        src={brand.picture320 ?? brand.picture ?? CompanyLogoPlaceholder}
                        className={clsxm(
                          "border-neutral-grey-lightest size-10 self-start rounded-full border",
                          !screenHeight && "size-8",
                        )}
                      />
                      <div className={clsxm("flex flex-col", screenHeight ? "gap-3" : "gap-1")}>
                        <span className="font-medium">{brand.company_name}</span>
                        <div className={clsxm("mt-1 flex gap-2", !screenHeight ? "flex-row gap-4" : "flex-col")}>
                          <button
                            onClick={() => {
                              setSelectedCompanyId(brand.id!);
                              setInviteModal({
                                isVisible: true,
                                isViewingOwnCompany: true,
                              });
                            }}
                            className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal transition-all hover:font-semibold">
                            <InviteUser className="size-3" />
                            {screenHeight && t("invite")}
                          </button>
                          <button
                            onClick={() => {
                              setSelectedCompanyId(brand.id!);
                              navigate(`/company/${brand.id}/edit/`);
                            }}
                            className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal no-underline transition-all hover:font-semibold">
                            <PenEdit className="size-3" />
                            {screenHeight && t("navigation:edit_company_profile")}
                          </button>
                        </div>
                      </div>
                    </div>
                    {selectedCompany?.id === brand.id ? (
                      <CheckIcon className="text-primary-medium-solid self-center" />
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedCompanyId(brand.id!);

                          if (!AGENCY_ROUTES_WITHOUT_NAVIGATION.includes(location.pathname)) {
                            navigate(`/company/${brand?.id}/dashboard/`);
                          }
                        }}
                        className="self-center">
                        <RightArrowIcon className="border-primary-light-solid text-neutral-grey-light size-8 rounded-[4px] border p-2.5" />
                      </button>
                    )}
                  </div>
                </Fragment>
              );
            })
          ) : (
            <>
              <div className="flex items-center gap-3 px-2.5 py-3">
                <img
                  className="size-10 rounded-full object-cover"
                  src={profileImage ?? (isCompany ? CompanyLogoPlaceholder : UserAvatarPlaceholder)}
                  alt="Profile"
                  style={{borderStyle: "solid"}}
                />
                <div className="flex flex-col gap-0.5">
                  <span className="text-base font-medium">{displayName}</span>
                  {user.has_multi_account_users_access && (
                    <Can I="create" a="CompanyMemberInvitation">
                      <button
                        onClick={() =>
                          setInviteModal({
                            isVisible: true,
                            isViewingOwnCompany: true,
                          })
                        }
                        className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal transition-all hover:font-semibold"
                        style={{
                          textDecoration: "none",
                        }}>
                        <InviteUser className="size-3" />
                        {t("invite")}
                      </button>
                    </Can>
                  )}
                  <Link
                    to={
                      isCompany ? `/company/${selectedCompany?.id}/edit/` : `/influencer/${user.influencer_uuid}/edit/`
                    }
                    className="text-primary-dark-solid flex flex-row items-center gap-1 text-xs font-normal transition-all hover:font-semibold"
                    style={{
                      textDecoration: "none",
                    }}>
                    <PenEdit className="size-3" />
                    {t(isCompany ? "edit_company_profile" : "edit_account")}
                  </Link>
                </div>
              </div>
              {isInfluencer && multiAccountAdminButtons}
              {isCompany && userPill}
            </>
          )}
        </div>
        <Link
          className="hover:bg-neutral-white-dark flex h-auto cursor-pointer gap-2 border-b px-2.5 py-3 text-xs font-normal"
          to={isCompany ? `/company/${selectedCompany?.id}/` : `/influencer/${user.influencer_uuid}/`}
          style={{
            textDecoration: "none",
          }}>
          {isCompany ? (
            <CompanyIcon className="h-4 w-auto shrink-0" />
          ) : (
            <UserProfileIcon className="h-4 w-auto shrink-0" />
          )}
          {t(isCompany ? "company_profile" : "your_profile")}
        </Link>
        {isCompany && (
          <div
            className="hover:bg-neutral-white-dark flex h-auto cursor-pointer gap-2 border-b px-2.5 py-3 text-xs font-normal"
            onClick={openUserProfile}>
            <UserProfileIcon className="h-4 w-auto shrink-0" />
            {t("your_profile")}
          </div>
        )}
        {isCompany && (
          <Can I="read" a="PaymentHistory">
            <Link
              className="hover:bg-neutral-white-dark flex h-auto cursor-pointer gap-2 border-b px-2.5 py-3 text-xs font-normal"
              to="/invoices"
              style={{
                textDecoration: "none",
              }}>
              <InvoiceIcon className="h-4 w-auto shrink-0" />
              {t("payment_history")}
            </Link>
          </Can>
        )}

        <a
          className="hover:bg-neutral-white-dark flex cursor-pointer gap-2 border-b px-2.5 py-3"
          style={{
            textDecoration: "none",
          }}
          href={helpUrl}>
          <HelpProfileIcon className="h-4 w-auto shrink-0" />
          <span className="text-xs font-normal">{t("help")}</span>
        </a>
        <div onClick={() => logout()} className="hover:bg-neutral-white-dark flex cursor-pointer gap-2 px-2.5 py-3">
          <LogoutProfileIcon className="h-4 w-auto shrink-0" />
          <span className="text-xs font-normal">{t("logout")}</span>
        </div>
      </div>
    </div>
  );
};

export default NavbarCompanyProfile;
